import { XMLParser, XMLValidator } from "fast-xml-parser";
import {
  datasetSchema,
  assertDataset,
  type Choice,
  type Dataset,
} from "@proof/core";

export interface LegacyImageRule {
  value: string;
  label: string;
  reference: string;
  previewReference: string;
}
type XmlNode = Record<string, unknown>;
const childName = (node: XmlNode) =>
  Object.keys(node).find(
    (key) => key !== ":@" && !key.startsWith("#") && !key.startsWith("?"),
  );
const children = (node: XmlNode, name = childName(node)) =>
  name && Array.isArray(node[name]) ? (node[name] as XmlNode[]) : [];
const text = (nodes: XmlNode[]): string =>
  nodes
    .map((node) =>
      typeof node["#text"] === "string" ? node["#text"] : text(children(node)),
    )
    .join("");
/** Extracts rule labels/references without evaluating XPath, opening paths or fetching URLs. */
export function pageflexImageRules(
  xml: string,
  variableName: string,
  ruleName: string,
): LegacyImageRule[] {
  if (
    typeof xml !== "string" ||
    new TextEncoder().encode(xml).length > 2000000 ||
    (xml.match(/</g)?.length || 0) > 30000
  )
    throw new Error("IMAGE_RULE_XML_LIMIT");
  if (/<!\s*(?:DOCTYPE|ENTITY)/i.test(xml))
    throw new Error("IMAGE_RULE_XML_DECLARATION");
  if (
    !variableName ||
    variableName.length > 200 ||
    !ruleName ||
    ruleName.length > 200
  )
    throw new Error("IMAGE_RULE_BINDING_INVALID");
  if (XMLValidator.validate(xml) !== true)
    throw new Error("IMAGE_RULE_XML_INVALID");
  let nodes: XmlNode[];
  try {
    nodes = new XMLParser({
      preserveOrder: true,
      ignoreAttributes: false,
      parseTagValue: false,
      trimValues: false,
      removeNSPrefix: true,
      maxNestedTags: 32,
    }).parse(xml);
  } catch {
    throw new Error("IMAGE_RULE_XML_INVALID");
  }
  const result: LegacyImageRule[] = [],
    seen = new Map<string, string>(),
    references = new Set<string>();
  const walk = (nodes: XmlNode[], depth: number) => {
    if (depth > 32) throw new Error("IMAGE_RULE_XML_LIMIT");
    for (const node of nodes) {
      const name = childName(node),
        attrs = node[":@"] as Record<string, string> | undefined;
      if (
        name === "var" &&
        attrs?.["@_kind"] === "Image" &&
        attrs["@_name"] === ruleName
      ) {
        const first = children(node).find((child) => childName(child)),
          lines = first ? children(first) : [];
        const nextResults: (XmlNode | undefined)[] = [];
        let nearest: XmlNode | undefined;
        for (let i = lines.length - 1; i >= 0; i--) {
          nextResults[i] = nearest;
          if (childName(lines[i]) === "var_rule_result") nearest = lines[i];
        }
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i],
            attr = line[":@"] as Record<string, string> | undefined;
          if (
            childName(line) !== "query_line" ||
            attr?.["@_category"] !== `<${variableName}>`
          )
            continue;
          const next = nextResults[i];
          if (!next) continue;
          const value = text(children(line)),
            reference = text(children(next)),
            previewReference = reference.replace(/\.pdf$/, ".jpg");
          if (!value.trim() || !reference.trim()) continue;
          if (value.length > 32768 || reference.length > 2000)
            throw new Error("IMAGE_RULE_VALUE_LIMIT");
          if (seen.has(value) && seen.get(value) !== previewReference)
            throw new Error("IMAGE_RULE_AMBIGUOUS_VALUE");
          if (references.has(previewReference)) continue;
          seen.set(value, previewReference);
          references.add(previewReference);
          result.push({ value, label: value, reference, previewReference });
          if (result.length > 5000) throw new Error("IMAGE_RULE_COUNT_LIMIT");
        }
      }
      walk(children(node), depth + 1);
    }
  };
  walk(nodes, 0);
  return result.sort((a, b) =>
    a.label < b.label ? -1 : a.label > b.label ? 1 : 0,
  );
}
export function bindImageRuleAssets(
  rules: LegacyImageRule[],
  assets: Readonly<Record<string, string>> = {},
): { choices: Choice[]; missing: string[] } {
  if (!Array.isArray(rules) || rules.length > 5000)
    throw new Error("IMAGE_RULE_COUNT_LIMIT");
  const missing: string[] = [];
  const choices = rules.map((rule) => {
    if (
      !rule ||
      [rule.value, rule.label, rule.reference, rule.previewReference].some(
        (s) => typeof s !== "string" || s.length > 32768,
      )
    )
      throw new Error("IMAGE_RULE_VALUE_INVALID");
    const assetId = Object.hasOwn(assets, rule.reference)
      ? assets[rule.reference]
      : Object.hasOwn(assets, rule.previewReference)
        ? assets[rule.previewReference]
        : undefined;
    if (assetId !== undefined && !/^asset_[a-zA-Z0-9_-]+$/.test(assetId))
      throw new Error("IMAGE_RULE_ASSET_INVALID");
    if (!assetId) missing.push(rule.reference);
    return {
      value: rule.value,
      label: rule.label,
      ...(assetId ? { assetId } : {}),
    };
  });
  return { choices, missing };
}
export function imageRuleDataset(input: {
  id: string;
  name: string;
  version?: number;
  xml: string;
  variableName: string;
  ruleName: string;
  assets?: Readonly<Record<string, string>>;
}): { dataset: Dataset; missing: string[] } {
  const mapped = bindImageRuleAssets(
    pageflexImageRules(input.xml, input.variableName, input.ruleName),
    input.assets,
  );
  const dataset = datasetSchema.parse({
    id: input.id,
    name: input.name,
    version: input.version ?? 1,
    kind: "choices",
    choices: mapped.choices,
  });
  assertDataset(dataset);
  return { dataset, missing: mapped.missing };
}
